import React, { useState, useEffect, useCallback } from 'react';
import { Trash2, Star, Upload, Image as ImageIcon } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { apiService } from '../../services/api';

interface CarImage {
  id: number;
  imageUrl: string;
  fileName: string;
  fileSize: number;
  isPrimary?: boolean;
}

interface ImageManagementProps {
  carListingId: number;
  currentImages?: CarImage[];
  onImagesChange: (images: CarImage[]) => void;
}

export function ImageManagement({ carListingId, currentImages = [], onImagesChange }: ImageManagementProps) {
  console.log('ImageManagement: Component rendered with carListingId:', carListingId);
  console.log('ImageManagement: Component rendered with currentImages:', currentImages);
  
  const [images, setImages] = useState<CarImage[]>(currentImages);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [objectUrls, setObjectUrls] = useState<string[]>([]);

  // Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      objectUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [objectUrls]);

  // Stable callback for onImagesChange
  const stableOnImagesChange = useCallback(onImagesChange, []);

  // Load existing images when editing a listing
  useEffect(() => {
    let isMounted = true;
    
    const loadExistingImages = async () => {
      console.log('ImageManagement: Loading images for carListingId:', carListingId);
      console.log('ImageManagement: carListingId type:', typeof carListingId);
      console.log('ImageManagement: currentImages prop:', currentImages);
      
      if (carListingId > 0) {
        setIsLoading(true);
        try {
          console.log('ImageManagement: Fetching images from API...');
          const existingImages = await apiService.getCarImages(carListingId);
          console.log('ImageManagement: Received images from API:', existingImages);
          console.log('ImageManagement: Number of images received:', existingImages.length);
          
          if (isMounted) {
            setImages(existingImages);
            stableOnImagesChange(existingImages);
          }
        } catch (error) {
          console.error('ImageManagement: Error loading existing images:', error);
          console.log('ImageManagement: Error details:', error);
          // Fallback to currentImages prop
          if (isMounted) {
            console.log('ImageManagement: Falling back to currentImages prop:', currentImages);
            setImages(currentImages);
            stableOnImagesChange(currentImages);
          }
        } finally {
          if (isMounted) {
            setIsLoading(false);
          }
        }
      } else {
        console.log('ImageManagement: carListingId is 0, using currentImages prop:', currentImages);
        if (isMounted) {
          setImages(currentImages);
          stableOnImagesChange(currentImages);
        }
      }
    };

    loadExistingImages();

    return () => {
      isMounted = false;
    };
  }, [carListingId, stableOnImagesChange]); // Added stableOnImagesChange back

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const newImages: CarImage[] = [];
      const newObjectUrls: string[] = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setUploadProgress((i / files.length) * 100);

        if (carListingId > 0) {
          // For existing listings, upload to backend
          const uploadedImage = await apiService.uploadCarImage(carListingId, file);
          newImages.push({
            id: uploadedImage.id,
            imageUrl: uploadedImage.imageUrl,
            fileName: uploadedImage.fileName,
            fileSize: uploadedImage.fileSize,
            isPrimary: images.length === 0 && i === 0
          });
        } else {
          // For new listings, create temporary image objects
          const tempId = Date.now() + i; // Temporary ID
          const tempImageUrl = URL.createObjectURL(file);
          newObjectUrls.push(tempImageUrl);
          newImages.push({
            id: tempId,
            imageUrl: tempImageUrl,
            fileName: file.name,
            fileSize: file.size,
            isPrimary: images.length === 0 && i === 0
          });
        }
      }

      const updatedImages = [...images, ...newImages];
      setImages(updatedImages);
      setObjectUrls(prev => [...prev, ...newObjectUrls]);
      stableOnImagesChange(updatedImages);
      setUploadProgress(100);
    } catch (error) {
      console.error('Error uploading images:', error);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDeleteImage = async (imageId: number) => {
    try {
      const imageToDelete = images.find(img => img.id === imageId);
      
      if (carListingId > 0 && imageToDelete) {
        // For existing listings, delete from backend
        await apiService.deleteFile(imageId, 'car_images');
      } else if (imageToDelete && imageToDelete.imageUrl.startsWith('blob:')) {
        // For temporary images, revoke the object URL
        URL.revokeObjectURL(imageToDelete.imageUrl);
        setObjectUrls(prev => prev.filter(url => url !== imageToDelete.imageUrl));
      }
      
      const updatedImages = images.filter(img => img.id !== imageId);
      setImages(updatedImages);
      stableOnImagesChange(updatedImages);
    } catch (error) {
      console.error('Error deleting image:', error);
    }
  };

  const handleSetPrimary = async (imageId: number) => {
    try {
      // Call backend API to set primary image
      await apiService.setPrimaryImage(carListingId, imageId);
      
      // Update local state
      const updatedImages = images.map(img => ({
        ...img,
        isPrimary: img.id === imageId
      }));
      setImages(updatedImages);
      stableOnImagesChange(updatedImages);
    } catch (error) {
      console.error('Error setting primary image:', error);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ImageIcon className="w-5 h-5" />
            Car Images Management
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Loading State */}
          {isLoading && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#f91bdc] mx-auto mb-2"></div>
              <p className="text-sm text-gray-500">Loading existing images...</p>
            </div>
          )}

          {/* Upload Section */}
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
            <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
            <p className="text-sm text-gray-600 mb-4">
              Upload car images (JPG, PNG, WebP up to 10MB each)
            </p>
            {carListingId === 0 && (
              <p className="text-xs text-orange-600 mb-2">
                ⚠️ Images will be uploaded when you save the listing
              </p>
            )}
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileUpload}
              disabled={isUploading}
              className="hidden"
              id="image-upload"
            />
            <label
              htmlFor="image-upload"
              className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#f91bdc] hover:bg-[#e016c7] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#f91bdc] cursor-pointer ${
                isUploading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isUploading ? 'Uploading...' : 'Upload Images'}
            </label>
            {isUploading && (
              <div className="mt-2">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-[#f91bdc] h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <p className="text-xs text-gray-500 mt-1">{Math.round(uploadProgress)}%</p>
              </div>
            )}
          </div>

          {/* Images Grid */}
          {!isLoading && images.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {images.map((image) => (
                <div key={`${image.id}-${image.fileName}`} className="relative group">
                  <div className="aspect-square rounded-lg overflow-hidden bg-gray-100">
                    <img
                      src={image.imageUrl}
                      alt={image.fileName}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        // Fallback to placeholder on error
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        const placeholder = target.nextElementSibling as HTMLDivElement;
                        if (placeholder) placeholder.style.display = 'flex';
                      }}
                    />
                    {/* Error fallback placeholder */}
                    <div className="absolute inset-0 bg-gradient-to-br from-[#f91bdc]/10 to-[#f91bdc]/20 flex items-center justify-center" style={{ display: 'none' }}>
                      <ImageIcon className="w-8 h-8 text-[#f91bdc]/60" />
                    </div>
                  </div>
                  
                  {/* Overlay with actions */}
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex gap-2">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => handleSetPrimary(image.id)}
                        className="bg-white text-gray-700 hover:bg-gray-50"
                        title={image.isPrimary ? 'Primary image' : 'Set as primary'}
                      >
                        <Star className={`w-4 h-4 ${image.isPrimary ? 'fill-yellow-400 text-yellow-400' : ''}`} />
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDeleteImage(image.id)}
                        className="bg-red-500 hover:bg-red-600"
                        title="Delete image"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Primary indicator */}
                  {image.isPrimary && (
                    <div className="absolute top-2 left-2 bg-yellow-400 text-yellow-900 px-2 py-1 rounded-full text-xs font-medium">
                      Primary
                    </div>
                  )}

                  {/* File info */}
                  <div className="mt-2 text-xs text-gray-500">
                    <p className="truncate">{image.fileName}</p>
                    <p>{(image.fileSize / 1024 / 1024).toFixed(1)} MB</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* No images message */}
          {!isLoading && images.length === 0 && !isUploading && (
            <div className="text-center py-8 text-gray-500">
              <ImageIcon className="w-12 h-12 mx-auto mb-2 text-gray-300" />
              <p>No images uploaded yet</p>
              <p className="text-sm">Upload images to showcase your car</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
